import { useEffect, useMemo, useState } from "react";
import { Target, AlertTriangle, TrendingUp, TrendingDown, Briefcase, Search, ChevronDown, ChevronRight, Droplets, Wrench, Truck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const ACCENT = "#EC4899";

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function fmtNum(n, frac = 1) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: frac, maximumFractionDigits: frac });
}
function fmtPct(p) {
  if (p === null || p === undefined || isNaN(p)) return "—";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(1)}%`;
}
function bandFor(pct) {
  if (pct === null || pct === undefined) return "unknown";
  if (pct > 20)  return "critical";
  if (pct > 0)   return "over";
  if (pct > -10) return "on_track";
  return "under";
}
const BAND_STYLES = {
  critical: { fg: "#F87171", chip: "border-red-500/40 bg-red-950/40 text-red-300",       label: "Critical" },
  over:     { fg: "#FBBF24", chip: "border-amber-500/40 bg-amber-950/40 text-amber-300", label: "Over" },
  on_track: { fg: "#34D399", chip: "border-emerald-500/40 bg-emerald-950/40 text-emerald-300", label: "On Track" },
  under:    { fg: "#60A5FA", chip: "border-sky-500/40 bg-sky-950/40 text-sky-300",        label: "Under" },
  unknown:  { fg: "#94A3B8", chip: "border-border bg-[#0A0B10] text-muted-foreground",   label: "—" },
};

const STAGE_META = {
  wash:  { Icon: Droplets, sf_source: "Wash Summary",  hours_filter: "phase=FIN · category in (ACID, SAND BLASTING)" },
  patch: { Icon: Wrench,   sf_source: "Patch Summary", hours_filter: "phase=FIN · category in (PATCH,PAT,PATCHING)" },
  load:  { Icon: Truck,    sf_source: "Load Summary",  hours_filter: "phase=FIN · category~LOAD" },
};

export default function FinishJobPerformanceCard({ showMexico = false }) {
  const [stages, setStages] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/departments/FINISH/job-performance-stages", { params: { show_mexico: showMexico } })
      .then((r) => !cancelled && setStages(r.data.stages))
      .catch((e) => !cancelled && toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [showMexico]);

  return (
    <section className="space-y-4" data-testid="finish-job-performance-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Briefcase size={18} style={{ color: ACCENT }} />
          <h3 className="font-heading font-bold text-xl">Job Performance · by Finish Stage</h3>
        </div>
      </div>

      {loading && (
        <div className="bg-[#12141D] border border-border py-12 text-center text-sm uppercase tracking-widest text-muted-foreground">
          Loading stages…
        </div>
      )}

      {!loading && stages && ["wash", "patch", "load"].map((key) => (
        <StageBlock
          key={key}
          stageKey={key}
          label={stages[key]?.label || key}
          budgetCategory={stages[key]?.budget_category || ""}
          rows={stages[key]?.rows || []}
          totals={stages[key]?.totals || {}}
        />
      ))}
    </section>
  );
}

function StageBlock({ stageKey, label, budgetCategory, rows, totals }) {
  const [bandFilter, setBandFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(true);
  const meta = STAGE_META[stageKey] || {};
  const Icon = meta.Icon || Briefcase;

  const filtered = useMemo(() => {
    let r = rows;
    if (bandFilter !== "all") r = r.filter((x) => bandFor(x.hours_pct) === bandFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => (x.job || "").toLowerCase().includes(q));
    }
    return r;
  }, [rows, bandFilter, search]);

  const counts = useMemo(() => {
    const out = { critical: 0, over: 0, on_track: 0, under: 0, unknown: 0 };
    rows.forEach((r) => { out[bandFor(r.hours_pct)] += 1; });
    return out;
  }, [rows]);

  const totalHrsPct = totals.budget_hours ? ((totals.actual_hours - totals.budget_hours) / totals.budget_hours) * 100 : null;
  const totalCostPct = totals.budget_cost ? ((totals.actual_cost - totals.budget_cost) / totals.budget_cost) * 100 : null;

  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden" data-testid={`finish-stage-${stageKey}`}>
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-[#1A1C28]/40 transition-colors"
        data-testid={`finish-stage-toggle-${stageKey}`}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronRight size={16} className="text-muted-foreground" />}
          <Icon size={16} style={{ color: ACCENT }} />
          <h4 className="font-heading font-bold text-base text-left">{label}</h4>
          <span className="text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
            · Category = {budgetCategory} · {rows.length} {rows.length === 1 ? "job" : "jobs"}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-4">
            <Tile label="Tracked Jobs" accent={ACCENT}
                  icon={<Briefcase size={12} />} value={rows.length}
                  testId={`tile-${stageKey}-jobs`} />
            <Tile label="Hours: A / B" accent={(totalHrsPct ?? 0) > 0 ? "#F87171" : "#34D399"}
                  icon={<TrendingUp size={12} />}
                  value={`${fmtNum(totals.actual_hours)} / ${fmtNum(totals.budget_hours)}`}
                  sub={fmtPct(totalHrsPct)} testId={`tile-${stageKey}-hours`} />
            <Tile label="Mat$: A / B" accent={(totalCostPct ?? 0) > 0 ? "#F87171" : "#34D399"}
                  icon={<TrendingDown size={12} />}
                  value={`${fmtMoney(totals.actual_cost)} / ${fmtMoney(totals.budget_cost)}`}
                  sub={fmtPct(totalCostPct)} testId={`tile-${stageKey}-cost`} />
            <Tile label="Actual SF (PASS)" accent="#34D399"
                  icon={<Target size={12} />}
                  value={fmtNum(totals.actual_sqft, 0)}
                  sub={`${fmtNum(totals.est_sqft, 0)} est`}
                  testId={`tile-${stageKey}-sf`} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="inline-flex border border-border">
              {[
                ["all",      `All (${rows.length})`],
                ["critical", `Critical (${counts.critical})`],
                ["over",     `Over (${counts.over})`],
                ["on_track", `On Track (${counts.on_track})`],
                ["under",    `Under (${counts.under})`],
              ].map(([k, lbl]) => (
                <button
                  key={k} type="button"
                  data-testid={`finish-${stageKey}-band-${k}`}
                  onClick={() => setBandFilter(k)}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                    bandFilter === k ? "bg-[#EC4899] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid={`finish-${stageKey}-search`}
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search job…"
                className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#EC4899]/60"
              />
            </div>
          </div>

          {rows.length === 0 && (
            <div className="py-10 text-center text-sm uppercase tracking-widest text-muted-foreground"
                 data-testid={`finish-${stageKey}-empty`}>
              No budget rows for category {budgetCategory} — verify Jobs Budget.xlsx
            </div>
          )}
          {rows.length > 0 && filtered.length === 0 && (
            <div className="py-10 text-center text-sm uppercase tracking-widest text-muted-foreground">
              No jobs match the filter
            </div>
          )}
          {filtered.length > 0 && (
            <div className="overflow-x-auto" data-testid={`finish-${stageKey}-table`}>
              <table className="w-full text-left text-xs">
                <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
                  <tr>
                    <th className="py-2.5 px-3">Job</th>
                    <th className="py-2.5 px-3 text-right">Est SF</th>
                    <th className="py-2.5 px-3 text-right">Act SF</th>
                    <th className="py-2.5 px-3 text-right">Pieces</th>
                    <th className="py-2.5 px-3 text-right">Budget Hrs</th>
                    <th className="py-2.5 px-3 text-right">Actual Hrs</th>
                    <th className="py-2.5 px-3 text-right w-[120px]">Hrs vs Budget</th>
                    <th className="py-2.5 px-3 text-right">Budget Mat$</th>
                    <th className="py-2.5 px-3 text-right">Actual Mat$</th>
                    <th className="py-2.5 px-3 text-right w-[120px]">Mat$ vs Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const hb = BAND_STYLES[bandFor(r.hours_pct)];
                    const cb = BAND_STYLES[bandFor(r.cost_pct)];
                    const hPct = Math.min(150, Math.abs(r.hours_pct ?? 0));
                    const cPct = Math.min(150, Math.abs(r.cost_pct ?? 0));
                    return (
                      <tr key={`${r.job}-${i}`}
                          className="border-t border-border/40 hover:bg-[#1A1C28]/40"
                          data-testid={`finish-${stageKey}-row-${r.job}`}>
                        <td className="py-3 px-3">
                          <div className="font-mono text-white">{r.job}</div>
                          <div className="text-[10px] text-muted-foreground truncate max-w-[180px]"
                               title={(r.categories || []).join(", ")}>
                            {(r.categories || []).join(", ")}
                          </div>
                        </td>
                        <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtNum(r.est_sqft, 0)}</td>
                        <td className="py-3 px-3 font-mono text-right">{fmtNum(r.actual_sqft, 0)}</td>
                        <td className="py-3 px-3 font-mono text-right text-muted-foreground">{r.pieces}</td>
                        <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtNum(r.budget_hours)}</td>
                        <td className="py-3 px-3 font-mono text-right">{fmtNum(r.actual_hours)}</td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 text-[9px] tracking-widest uppercase border font-mono ${hb.chip}`}>
                              {hb.label}
                            </span>
                            <span className="font-mono" style={{ color: hb.fg }}>{fmtPct(r.hours_pct)}</span>
                          </div>
                          <div className="relative h-1 bg-[#1F2230] mt-1 overflow-hidden">
                            <div className="absolute top-0 left-0 h-full transition-all"
                                 style={{ width: `${hPct}%`, backgroundColor: hb.fg }} />
                          </div>
                        </td>
                        <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtMoney(r.budget_cost)}</td>
                        <td className="py-3 px-3 font-mono text-right">{fmtMoney(r.actual_cost)}</td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 text-[9px] tracking-widest uppercase border font-mono ${cb.chip}`}>
                              {cb.label}
                            </span>
                            <span className="font-mono" style={{ color: cb.fg }}>{fmtPct(r.cost_pct)}</span>
                          </div>
                          <div className="relative h-1 bg-[#1F2230] mt-1 overflow-hidden">
                            <div className="absolute top-0 left-0 h-full transition-all"
                                 style={{ width: `${cPct}%`, backgroundColor: cb.fg }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Tile({ label, value, sub, icon, accent, testId }) {
  return (
    <div data-testid={testId} className="bg-[#12141D] p-4 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="font-heading font-bold text-2xl tabular-nums" style={{ color: accent }}>
        {value ?? "—"}
      </div>
      {sub && <div className="text-[11px] font-mono text-muted-foreground">{sub}</div>}
    </div>
  );
}
